import firebase from 'firebase';
import { isUndefined } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { onRegisterFirebaseToken } from '~/api/notifications';
import { API_CONFIG } from '~/config/constants';
import { useConfig } from '~/contexts/ConfigContext';
import { useAppSelector } from '~/redux/hooks';
import { setIsMaintenance } from '~/redux/modules/authentication';
import FireStore from '~/utils/Firestore';
import { onGetDataFromFireStore } from '~/utils/helpers';
import useBranding from './useBranding';
import useWebHistory from './useWebHistory';

const useShowMaintenance = () => {
    const dispatch = useDispatch();
    const { branding, appName } = useBranding();
    const [isShow, setIsShow] = useState<boolean>();
    const location = useLocation();
    const history = useWebHistory();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { config } = useConfig();

    const { id, isAuthed, isInitializedFirebase } = useAppSelector(({ profile, authentication }) => ({ ...profile, ...authentication }));

    const onRedirectToMaintenance = () => {
        history.push('/maintenance');
    };

    // '/' rather than '/pinboard'. Pinboard is a member-app route that came
    // across with this hook and does not exist here, so coming out of
    // maintenance dropped a signed-in user on the not-found page.
    const onRedirectToHome = () => {
        history.push(isAuthed ? '/' : '/login');
    };

    const onShow = async (isShow: boolean) => {
        setIsShow(isShow);
        dispatch(setIsMaintenance({
            isMaintenance: isShow
        }));

        if (isShow && isAuthed) {
            if (firebase.messaging.isSupported()) {
                const messaging = firebase.messaging();

                await messaging.deleteToken();
            }

            await onRegisterFirebaseToken(id!, '');
        }
    };

    const subscribe = useCallback(() => {
        if (!isInitializedFirebase || !config.FIREBASE_clientId) return;

        const fireStore = new FireStore(branding.firestoreMiscCollection);

        return fireStore.getStore()
            .collection(branding.firestoreMiscCollection)
            .doc(`${config.APP_TYPE}/${config.FIRESTORE_DB_ENV_VERSION_NAME}/${config.OS_TYPE}`)
            .onSnapshot((querySnapshot: any) => {
                const data: any = onGetDataFromFireStore(querySnapshot);

                if (data) {
                    onShow(data?.is_maintenance);
                }
            });
    }, [isAuthed, id, API_CONFIG.API_ID, appName, branding.firestoreMiscCollection, isInitializedFirebase, location.pathname]);

    useEffect(() => {
        const subscriber = subscribe();

        return () => {
            if (subscriber) subscriber();
        };
    }, [isInitializedFirebase]);

    useEffect(() => {
        if (isShow) onRedirectToMaintenance();
        if (!isShow && !isUndefined(isShow) && location.pathname?.toLowerCase()?.includes('maintenance')) onRedirectToHome();
    }, [isShow, location.pathname]);

    return {
        isLoading
    };
};

export default useShowMaintenance;
